import { Helmet } from 'react-helmet-async';
import { useEffect } from 'react';

const SITE = 'https://nirmandwar.com';

export const SEO = ({ title, description, canonical, jsonLd, noindex = false }) => {
  const meta = [];
  if (description) {
    meta.push({ name: 'description', content: description });
    meta.push({ property: 'og:description', content: description });
    meta.push({ name: 'twitter:description', content: description });
  }
  if (noindex) meta.push({ name: 'robots', content: 'noindex, nofollow' });
  if (title) {
    meta.push({ property: 'og:title', content: title });
    meta.push({ name: 'twitter:title', content: title });
  }

  const link = [];
  if (canonical) link.push({ rel: 'canonical', href: canonical.startsWith('http') ? canonical : `${SITE}${canonical}` });

  // Inject JSON-LD via DOM to avoid react-helmet-async script children issues
  useEffect(() => {
    if (!jsonLd) return;
    const schemas = Array.isArray(jsonLd) && jsonLd[0] && jsonLd[0]['@context'] ? jsonLd : [jsonLd];
    const ids = [];
    schemas.forEach((schema, i) => {
      const id = `jsonld-seo-${i}`;
      ids.push(id);
      let el = document.getElementById(id);
      if (!el) {
        el = document.createElement('script');
        el.id = id;
        el.type = 'application/ld+json';
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(schema);
    });
    return () => { ids.forEach(id => document.getElementById(id)?.remove()); };
  }, [jsonLd]);

  return <Helmet title={title || undefined} meta={meta} link={link} />;
};

export const NoIndex = ({ children }) => (
  <>
    <Helmet meta={[{ name: 'robots', content: 'noindex, nofollow' }]} />
    {children}
  </>
);

// JSON-LD helpers
export const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'NirmanDwar',
  url: SITE,
  logo: `${SITE}/logo512.png`,
  description: 'India\'s hyperlocal construction material marketplace connecting buyers with verified local suppliers for cement, TMT, paint, bricks, and more.',
  contactPoint: { '@type': 'ContactPoint', email: 'support@nirmandwar.com', contactType: 'customer service' },
  sameAs: [],
};

export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'NirmanDwar',
  url: SITE,
  potentialAction: {
    '@type': 'SearchAction',
    target: { '@type': 'EntryPoint', urlTemplate: `${SITE}/buyer-dashboard?q={search_term_string}` },
    'query-input': 'required name=search_term_string',
  },
};

export const breadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: item.name,
    item: item.url ? (item.url.startsWith('http') ? item.url : `${SITE}${item.url}`) : undefined,
  })),
});

export const faqSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(f => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
});
